import React, { useMemo } from 'react'
import { Panel } from '@components/Panel/Panel'
import { TokenImage } from '@components/Tokens/TokenSelector/TokenImage'
import { useReadableMembershipInfo } from '@components/TownPageLayout/useReadableMembershipInfo'
import { Icon, Paragraph, Stack, Text } from '@ui'
import { useNftMetadata } from 'api/lib/tokenContracts'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useEnvironment } from 'hooks/useEnvironment'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { shimmerClass } from 'ui/styles/globals/shimmer.css'
import {
    SpaceAddressFromSpaceId,
    TransformedSubscription,
    useConnectivity,
    useGetSubscriptions,
    useLinkedAndDelegatedWallets,
    useMembershipRenewalPrice,
    useMultipleMembershipStatus,
    useMyUserId,
    useOfflineStore,
} from 'use-towns-client'
import { shouldDisplayFreeText } from 'utils/shouldDisplayFreeText'
import { formatEther, formatUnits } from 'viem'

import { toCountdownData } from '../RenewMembership/hooks/useRenewCountdown'
import { needsCurrencyMigration } from '../utils'
import { nftToToken } from '../Wallet/TransferOwnership/TransferOwnershipPanel'

import { useMySpaceStreams } from './hooks/useMySpaceStreams'

export function SubscriptionsPanel() {
    const { loggedInWalletAddress } = useConnectivity()
    // We probably do not need to pass wallets with delegations here, being extra cautious.
    const { data: wallets } = useLinkedAndDelegatedWallets({ walletAddress: loggedInWalletAddress })
    const userId = useMyUserId()

    const { joined: joinedSpaceIds, left: leftSpaceIds } = useMySpaceStreams()

    // check all the onchain memberships for the user because subscriptions could be active for spaces that the user has left
    // also, maybe the subscription failed to install, but we still want to show the user the space and allow them to install it
    const spaceIds = useMemo(
        () => joinedSpaceIds.concat(leftSpaceIds),
        [joinedSpaceIds, leftSpaceIds],
    )

    const { data: membershipStatuses, isLoading: isLoadingMembershipStatuses } =
        useMultipleMembershipStatus({
            spaceIds: spaceIds,
            userId: userId || '',
            wallets: wallets || [],
            batchSize: 5,
        })

    const { data: myAddress } = useMyAbstractAccountAddress()

    const { data: subscriptions, isLoading: isLoadingSubscriptions } = useGetSubscriptions({
        walletAddress: myAddress ?? '',
    })

    const isLoading = isLoadingMembershipStatuses || isLoadingSubscriptions

    // this is populated via hooks in use-space-data that should have already been fetched
    const offlineSpaceInfoMap = useOfflineStore((s) => s.offlineSpaceInfoMap)

    // ⚠️ NOTE: this list filters out spaces where the user is the owner & where their membership has a 0n expiry, which signifiees an infinite duration
    // infinite duration is granted on the token ID 0 for a space - the membership nft that is minted to the user that creates the space
    // if you were the space owner and you then transferred your membership nft to someone else, the person now has a infinite duration membership
    // this probably won't be a thing for any real users, but it can be confusing for test users when you intentionally transfer membership nfts to test other things

    const spacesByActiveAndExpired = useMemo(() => {
        return spaceIds.reduce<{ active: string[]; expired: string[] }>(
            (acc, spaceId) => {
                const isOwner = wallets
                    ?.map((w) => w.toLowerCase())
                    .includes(offlineSpaceInfoMap[spaceId]?.owner?.toLowerCase() ?? '')

                if (!isOwner) {
                    const membershipStatus = membershipStatuses.get(spaceId)

                    if (membershipStatus?.isExpired) {
                        // if the user has left the space and the membership has expired, we don't need to show it
                        if (leftSpaceIds.includes(spaceId)) {
                            return acc
                        }
                        acc.expired.push(spaceId)
                    } else if (
                        membershipStatus?.expiryTime !== undefined &&
                        // 0n = infinite duration
                        membershipStatus?.expiryTime !== 0n
                    ) {
                        acc.active.push(spaceId)
                    }
                }
                return acc
            },
            {
                active: [],
                expired: [],
            },
        )
    }, [spaceIds, wallets, offlineSpaceInfoMap, membershipStatuses, leftSpaceIds])

    if (!wallets || !userId) {
        return null
    }

    return (
        <Panel label="Memberships">
            {isLoading ? (
                <Stack gap="lg">
                    <ButtonSpinner />
                </Stack>
            ) : (
                <Stack gap="lg">
                    {spacesByActiveAndExpired.active.length === 0 &&
                        spacesByActiveAndExpired.expired.length === 0 && (
                            <Stack
                                centerContent
                                gap="sm"
                                position="relative"
                                overflow="auto"
                                rounded="sm"
                                background="level2"
                                padding="lg"
                            >
                                <Paragraph>You have no subscriptions</Paragraph>
                            </Stack>
                        )}
                    {/* active */}
                    {spacesByActiveAndExpired.active.length > 0 && (
                        <Stack grow gap="sm" position="relative" overflow="auto">
                            <>
                                <Paragraph strong size="lg">
                                    Active
                                </Paragraph>
                                {spacesByActiveAndExpired.active.map((spaceId) => {
                                    const membershipStatus = membershipStatuses.get(spaceId)

                                    if (!membershipStatus) {
                                        return (
                                            <Stack
                                                horizontal
                                                gap
                                                alignItems="center"
                                                padding="md"
                                                key={spaceId}
                                                rounded="sm"
                                                background="level2"
                                                className={shimmerClass}
                                                style={{
                                                    height: '80px',
                                                }}
                                            >
                                                <Stack>
                                                    <Stack
                                                        rounded="sm"
                                                        width="x5"
                                                        aspectRatio="1/1"
                                                        background="level3"
                                                    />
                                                </Stack>
                                                {/* <Stack gap="sm">
                                    <Text strong size="lg">
                                        {space.name}
                                    </Text>
                                </Stack> */}
                                            </Stack>
                                        )
                                    }

                                    if (membershipStatus.isMember && !membershipStatus.isExpired) {
                                        return (
                                            <SubscriptionItem
                                                key={spaceId}
                                                spaceId={spaceId}
                                                userId={userId || ''}
                                                spaceName={offlineSpaceInfoMap[spaceId]?.name ?? ''}
                                                leftSpaceIds={leftSpaceIds}
                                                membershipStatus={membershipStatus}
                                                subscriptions={subscriptions}
                                            />
                                        )
                                    }

                                    return null
                                })}
                            </>
                        </Stack>
                    )}

                    {/* expired */}
                    {spacesByActiveAndExpired.expired.length > 0 && (
                        <>
                            <Stack grow gap="sm" position="relative" overflow="auto">
                                <Paragraph strong size="lg">
                                    Expired
                                </Paragraph>
                                {spacesByActiveAndExpired.expired.map((spaceId) => {
                                    const membershipStatus = membershipStatuses.get(spaceId)

                                    if (membershipStatus?.isExpired) {
                                        return (
                                            <SubscriptionItem
                                                key={spaceId}
                                                spaceId={spaceId}
                                                userId={userId || ''}
                                                spaceName={offlineSpaceInfoMap[spaceId]?.name ?? ''}
                                                leftSpaceIds={leftSpaceIds}
                                                membershipStatus={membershipStatus}
                                            />
                                        )
                                    }

                                    return null
                                })}
                            </Stack>
                        </>
                    )}
                </Stack>
            )}
        </Panel>
    )
}

function getSubscription(subscriptions: TransformedSubscription[], spaceId: string) {
    return subscriptions.find(
        (subscription) =>
            subscription.space.toLowerCase() === SpaceAddressFromSpaceId(spaceId)?.toLowerCase(),
    )
}

function SubscriptionItem(args: {
    spaceId: string
    userId: string
    spaceName: string
    subscriptions?: TransformedSubscription[] | undefined
    // space ids that the user has left
    leftSpaceIds: string[]
    membershipStatus: {
        isMember: boolean
        tokenId?: string
        isExpired?: boolean
        expiryTime?: bigint
        expiredAt?: bigint
    }
}) {
    const { spaceName, spaceId } = args

    const isLeftSpace = args.leftSpaceIds.includes(spaceId)

    const subscription = useMemo(
        () => getSubscription(args.subscriptions ?? [], spaceId),
        [args.subscriptions, spaceId],
    )
    const { membershipInfo, membershipRenewalPrice, countdownData, token } =
        useSubscriptionDisplayData(args)

    const isUsdc = membershipInfo?.currency === 'USDC'

    const { openPanel } = usePanelActions()
    const renewalPriceFormatted = useMemo(() => {
        return isUsdc
            ? formatUnits(membershipRenewalPrice ?? BigInt(0), 6)
            : formatEther(membershipRenewalPrice ?? BigInt(0))
    }, [membershipRenewalPrice, isUsdc])

    const displayPrice = useMemo(() => {
        if (shouldDisplayFreeText(renewalPriceFormatted)) {
            return 'Free'
        }
        return `${renewalPriceFormatted} ${membershipInfo?.currency}`
    }, [renewalPriceFormatted, membershipInfo?.currency])

    if (isLeftSpace && (!subscription || !subscription.active)) {
        return null
    }

    const needsMigration =
        subscription?.active &&
        membershipInfo?.currency &&
        needsCurrencyMigration(subscription.currency, membershipInfo.currency)

    return (
        <Stack
            horizontal
            hoverable
            gap="sm"
            background="level2"
            paddingX="sm"
            paddingY="md"
            rounded="sm"
            cursor="pointer"
            onClick={() => {
                openPanel(CHANNEL_INFO_PARAMS.SINGLE_SUBSCRIPTION, {
                    spaceId,
                })
            }}
        >
            <TokenImage imgSrc={token?.data.imgSrc} width="x5" height="x5" background="inherit" />
            <Stack gap="sm">
                <Text strong size="lg">
                    {spaceName}
                </Text>
                <Stack horizontal gap="xs" alignItems="center">
                    <Text>{displayPrice}</Text>
                    <Stack
                        as="span"
                        rounded="full"
                        background="transparent40"
                        style={{
                            width: '5px',
                            height: '5px',
                        }}
                    />
                    <Text>{readableDuration(membershipInfo?.duration ?? 0)}</Text>
                </Stack>
                {countdownData.expiryDate && !needsMigration && (
                    <ExpiryDate
                        expiryDate={countdownData.expiryDate}
                        active={subscription?.active}
                    />
                )}
                {isLeftSpace && subscription?.active && (
                    <Stack horizontal alignItems="center" gap="sm">
                        <Icon size="square_xs" color="negative" type="alert" />
                        <Text size="sm" color="gray1">
                            No longer a member of this town.
                        </Text>
                    </Stack>
                )}
                {needsMigration && (
                    <Stack horizontal alignItems="center" gap="sm">
                        <Icon size="square_xs" color="negative" type="alert" />
                        <Text size="sm" color="negative">
                            Needs update
                        </Text>
                    </Stack>
                )}
            </Stack>
        </Stack>
    )
}

export function ExpiryDate(args: { expiryDate: string; active: boolean | undefined }) {
    const { expiryDate, active } = args
    return (
        <Text size="sm" color={active ? 'gray2' : 'negative'}>
            {active ? 'Renews on' : 'Expires on'} {expiryDate}
        </Text>
    )
}

export const readableDuration = (duration: number) => {
    const MINUTE = 60
    const HOUR = 60 * MINUTE
    const DAY = 24 * HOUR
    const WEEK = 7 * DAY
    const MONTH = 30 * DAY // Approximate
    const YEAR = 365 * DAY

    // Check for standard durations
    if (duration >= YEAR) {
        const years = Math.floor(duration / YEAR)
        return years === 1 ? 'Yearly' : `${years} Years`
    }

    if (duration >= MONTH) {
        const months = Math.floor(duration / MONTH)
        return months === 1 ? 'Monthly' : `${months} Months`
    }

    if (duration >= WEEK) {
        const weeks = Math.floor(duration / WEEK)
        return weeks === 1 ? 'Weekly' : `${weeks} Weeks`
    }

    // For durations less than a week, show detailed time
    const days = Math.floor(duration / DAY)
    const hours = Math.floor((duration % DAY) / HOUR)
    const minutes = Math.floor((duration % HOUR) / MINUTE)

    const parts = []
    if (days > 0) {
        parts.push(`${days}d`)
    }
    if (hours > 0) {
        parts.push(`${hours}h`)
    }
    if (minutes > 0) {
        parts.push(`${minutes}m`)
    }

    return parts.length > 0 ? parts.join(' ') : 'Less than 1 minute'
}

export function useSubscriptionDisplayData(args: {
    spaceId: string
    membershipStatus: {
        isMember: boolean
        tokenId?: string
        isExpired?: boolean
        expiryTime?: bigint
        expiredAt?: bigint
    }
    spaceName: string
}) {
    const { spaceId, membershipStatus } = args
    const chainId = useEnvironment().baseChain.id
    const { data: tokenData } = useNftMetadata({
        chainId,
        tokenId: membershipStatus.tokenId ?? '',
        contractAddress: SpaceAddressFromSpaceId(spaceId) ?? '',
    })
    const { data: membershipInfo } = useReadableMembershipInfo(spaceId)
    const { data: membershipRenewalPrice } = useMembershipRenewalPrice({
        spaceId,
        tokenId: membershipStatus.tokenId ?? '',
    })
    const countdownData = toCountdownData({
        isMember: membershipStatus.isMember,
        isExpired: membershipStatus.isExpired,
        expiryTime: membershipStatus.expiryTime,
        displayWindowDays: 0,
    })

    const token = nftToToken(tokenData, chainId)

    return {
        membershipInfo,
        membershipRenewalPrice,
        countdownData,
        token,
    }
}
